import requestApi, { HTTP_METHOD } from '@/utils/axios.http';
import { TGroupSchema } from '@/schemas/group/group.schema';
import { StructureAdvancedFilter } from '@/entities/structure/group';
import { mapStructureFilterAPI } from '@/utils/map.utils';
import {
  ListAllDistributor,
  ListAllGroup,
  TotalActiveGroup,
  UploadGroupCustomImageDTO,
} from '../entities/group';
import { TOKEN_API_PUBLIC } from '@/utils/constant.utils';

const GROUP_URI = '/group';

export const createNewGroup = async (group: TGroupSchema) => {
  return requestApi({
    url: GROUP_URI,
    method: HTTP_METHOD.POST,
    body: group,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getExistingGroup = async (query?: TGroupSchema) => {
  let url = `${GROUP_URI}/existing`;
  const params = new URLSearchParams();

  if (query?.id) {
    params.append('id', query.id);
  }

  if (query?.name) {
    params.append('name', query.name);
  }

  if (query?.businessIdNumber) {
    params.append('businessIdNumber', query.businessIdNumber);
  }

  if (query?.email) {
    params.append('email', query.email);
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return requestApi({
    url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const listAllGroups = async (query?: ListAllGroup) => {
  let url = `${GROUP_URI}/allGroups`;
  const params = new URLSearchParams();

  if (query?.userId) {
    params.append('userId', query.userId);
  }

  if (query?.distributorId) {
    params.append('distributorId', query.distributorId);
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return requestApi({
    url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
      'x-api-token': TOKEN_API_PUBLIC,
    },
  });
};

export const getAllGroups = async (
  filter: StructureAdvancedFilter,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  const filterMapped = mapStructureFilterAPI(filter);
  let url = `${GROUP_URI}/all?${filterMapped}&page=${page}&limit=${limit}`;

  // Add sort parameters if provided
  if (sortBy) {
    url += `&sortBy=${sortBy}&sortDirection=${sortDirection || 'asc'}`;
  }
  return requestApi({
    url: url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getTotalActiveGroups = async (query?: TotalActiveGroup) => {
  return requestApi({
    url: `${GROUP_URI}/totalActive${
      query?.userId ? `?userId=${query?.userId}` : ''
    }`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getGroupById = async (id: string) => {
  return requestApi({
    url: `${GROUP_URI}/${id}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
      'x-api-token': TOKEN_API_PUBLIC,
    },
  });
};

export const getFirstGroupByVehiculeId = async (vehiculeId: string) => {
  return requestApi({
    url: `${GROUP_URI}/vehicule/${vehiculeId}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const listAllDistributors = async (query?: ListAllDistributor) => {
  let url = `${GROUP_URI}/allDistributors`;
  const params = new URLSearchParams();

  if (query?.userId) {
    params.append('userId', query?.userId);
  }

  if (query?.excludeDefault) {
    params.append('excludeDefault', query?.excludeDefault?.toString());
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return requestApi({
    url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getUnassignedGroupsToUser = async (userId: string) => {
  return requestApi({
    url: `${GROUP_URI}/unassigned/user/${userId}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getAssignedGroupsToUser = async (userId: string) => {
  return requestApi({
    url: `${GROUP_URI}/assigned/user/${userId}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getGroupsEndingSubscriptions = async (userId?: string) => {
  let url = `${GROUP_URI}/get/endingSubscriptions`;
  const params = new URLSearchParams();

  if (userId) {
    params.append('userId', userId);
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return requestApi({
    url: url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
      'x-api-token': TOKEN_API_PUBLIC,
    },
  });
};

export const updateGroup = async (group: TGroupSchema) => {
  return requestApi({
    url: `${GROUP_URI}/update`,
    method: HTTP_METHOD.PATCH,
    body: group,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const updateManyGroups = async (groups: TGroupSchema[]) => {
  return requestApi({
    url: `${GROUP_URI}/updateMany`,
    method: HTTP_METHOD.PATCH,
    body: groups,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getLegalInformation = async (userId: string, roleName: string) => {
  return requestApi({
    url: `${GROUP_URI}/legalInformation/${userId}/${roleName}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const uploadGroupCustomImage = async (
  filename: string,
  data: UploadGroupCustomImageDTO,
) => {
  return requestApi({
    url: `${GROUP_URI}/uploadImageFile/${data?.groupId}?filename=${filename}`,
    method: HTTP_METHOD.POST,
    body: data?.formData,
    headers: {
      'Content-Type': 'multipart/form-data',
      'x-api-token': TOKEN_API_PUBLIC,
    },
  });
};
